import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Check, Loader2, PartyPopper } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { COMPANY, SERVICE_OPTIONS, FREQUENCIES, HOUR_OPTIONS } from "../../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const field =
  "w-full bg-transparent border border-[#D5D1C8] rounded-xl px-4 py-3.5 text-[#1A362D] placeholder:text-[#7B8A83] focus:outline-none focus:border-[#1A362D] focus:ring-1 focus:ring-[#1A362D] transition-colors duration-200";

export const BookingForm = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", suburb: "", frequency: "", hours: "", message: "" });
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (s) => setServices((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.suburb) {
      toast.error("Please fill in your name, email, phone and suburb.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/bookings`, { ...form, services });
      setDone(data.ref);
      toast.success("Request received — we'll be in touch shortly!");
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="book" data-testid="booking-section" className="py-28 md:py-36 bg-[#D1DEC9] relative overflow-hidden grain">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-14 relative z-10">
        <Reveal className="md:col-span-5">
          <p className="text-xs md:text-sm font-semibold tracking-[0.25em] uppercase text-[#C26D5C] mb-6">Request a quote</p>
          <h2 className="font-serif font-light text-5xl md:text-6xl text-[#1A362D] tracking-tight leading-[1.05]">
            Let's give you your time back.
          </h2>
          <p className="mt-6 text-lg text-[#4A5A53] leading-relaxed max-w-md">
            Tell us a little about your home and what you need help with. We'll reply with a clear,
            no-obligation quote and match you with the right assistant.
          </p>
          <div className="mt-10 space-y-2 text-[#1A362D]">
            <p className="font-serif text-2xl">{COMPANY.phone}</p>
            <p className="text-[#4A5A53]">{COMPANY.email}</p>
            <p className="text-[#7B8A83] text-sm tracking-wide uppercase mt-3">Serving {COMPANY.area}</p>
          </div>
        </Reveal>

        <Reveal delay={0.1} className="md:col-span-7">
          <div className="rounded-[2rem] bg-[#F9F8F6] border border-[#D5D1C8] p-8 md:p-10">
            {done ? (
              <div data-testid="booking-success" className="py-12 text-center flex flex-col items-center">
                <span className="w-16 h-16 rounded-full bg-[#D1DEC9] flex items-center justify-center">
                  <PartyPopper size={28} className="text-[#1A362D]" />
                </span>
                <h3 className="mt-6 font-serif text-4xl text-[#1A362D]">Thank you!</h3>
                <p className="mt-3 text-[#4A5A53] max-w-sm">
                  Your request has been received. Reference <span className="font-semibold text-[#1A362D]">{done}</span>.
                  We'll be in touch within one business day.
                </p>
                <button
                  data-testid="booking-reset"
                  onClick={() => { setDone(null); setForm({ name: "", email: "", phone: "", suburb: "", frequency: "", hours: "", message: "" }); setServices([]); }}
                  className="mt-8 rounded-full border border-[#1A362D] text-[#1A362D] px-6 py-3 text-sm font-medium hover:bg-[#1A362D] hover:text-[#F9F8F6] transition-colors duration-300"
                >
                  Send another request
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="booking-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <input data-testid="input-name" className={field} placeholder="Full name *" value={form.name} onChange={set("name")} />
                  <input data-testid="input-phone" className={field} placeholder="Phone *" value={form.phone} onChange={set("phone")} />
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <input data-testid="input-email" type="email" className={field} placeholder="Email *" value={form.email} onChange={set("email")} />
                  <input data-testid="input-suburb" className={field} placeholder="Suburb *" value={form.suburb} onChange={set("suburb")} />
                </div>

                <div>
                  <p className="text-sm font-medium text-[#4A5A53] mb-3">What can we help with?</p>
                  <div className="flex flex-wrap gap-2.5">
                    {SERVICE_OPTIONS.map((s) => {
                      const active = services.includes(s);
                      return (
                        <button
                          type="button"
                          key={s}
                          data-testid={`service-chip-${s.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                          onClick={() => toggle(s)}
                          className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm border transition-colors duration-200 ${
                            active ? "bg-[#1A362D] text-[#F9F8F6] border-[#1A362D]" : "bg-transparent text-[#4A5A53] border-[#D5D1C8] hover:border-[#1A362D]"
                          }`}
                        >
                          {active && <Check size={14} />}
                          {s}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <select data-testid="select-frequency" className={`${field} appearance-none`} value={form.frequency} onChange={set("frequency")}>
                    <option value="">Frequency</option>
                    {FREQUENCIES.map((f) => <option key={f} value={f}>{f}</option>)}
                  </select>
                  <select data-testid="select-hours" className={`${field} appearance-none`} value={form.hours} onChange={set("hours")}>
                    <option value="">Hours per visit</option>
                    {HOUR_OPTIONS.map((h) => <option key={h} value={h}>{h}</option>)}
                  </select>
                </div>

                <textarea data-testid="input-message" rows={4} className={field} placeholder="Anything else we should know?" value={form.message} onChange={set("message")} />

                <motion.button
                  data-testid="booking-submit"
                  type="submit"
                  disabled={loading}
                  whileTap={{ scale: 0.98 }}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#C26D5C] text-[#F9F8F6] px-7 py-4 text-sm font-medium hover:bg-[#A85D4E] transition-colors duration-300 disabled:opacity-60"
                >
                  {loading ? <Loader2 size={18} className="animate-spin" /> : null}
                  {loading ? "Sending..." : "Send my request"}
                </motion.button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
